import { useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard, Users, FileText, AlertTriangle, ShieldAlert,
  Receipt, ScrollText, FileCheck2, BookOpen, LogOut, Shield, Sparkles, Zap, Activity, BadgeCheck,
  Banknote, Mail, Command as CommandIcon, Trophy, Home, KeyRound, UserCog, Settings as SettingsIcon, Webhook, Inbox,
} from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationsBell from "./NotificationsBell";
import CmdKPalette from "./CmdKPalette";
import ApiStatusBadge from "./ApiStatusBadge";
import { toast } from "sonner";
import { API } from "../lib/api";

export default function AdminLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const sseRef = useRef(null);

  const NAV = [
    { to: "/app", label: t("admin_nav.dashboard"), icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/merchants", label: t("admin_nav.merchants"), icon: Users, testid: "nav-merchants" },
    { to: "/policies", label: t("admin_nav.policies"), icon: FileText, testid: "nav-policies" },
    { to: "/claims", label: t("admin_nav.claims"), icon: AlertTriangle, testid: "nav-claims" },
    { to: "/payouts", label: "Payouts", icon: Banknote, testid: "nav-payouts" },
    { to: "/fraud", label: t("admin_nav.fraud_queue"), icon: ShieldAlert, testid: "nav-fraud" },
    { to: "/loyalty", label: t("admin_nav.loyalty"), icon: Sparkles, testid: "nav-loyalty" },
    { to: "/referrals", label: "Referrals", icon: Trophy, testid: "nav-referrals" },
    { to: "/ledger", label: t("admin_nav.premium_ledger"), icon: Receipt, testid: "nav-ledger" },
    { to: "/audit", label: t("admin_nav.audit_logs"), icon: ScrollText, testid: "nav-audit" },
    { to: "/kyc", label: t("admin_nav.kyc_review"), icon: BadgeCheck, testid: "nav-kyc" },
    { to: "/compliance", label: t("admin_nav.compliance"), icon: FileCheck2, testid: "nav-compliance" },
    { to: "/digest", label: "Email Digest", icon: Mail, testid: "nav-digest" },
    { to: "/incidents", label: "Status & Incidents", icon: Activity, testid: "nav-incidents" },
    { to: "/security", label: "Security", icon: KeyRound, testid: "nav-security" },
    { to: "/webhooks", label: "Webhooks", icon: Webhook, testid: "nav-webhooks" },
    { to: "/leads", label: "Inbox", icon: Inbox, testid: "nav-leads" },
    ...(user?.role === "super_admin"
      ? [{ to: "/users", label: "Users", icon: UserCog, testid: "nav-users" }]
      : []),
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
    { to: "/kfs", label: t("admin_nav.key_facts"), icon: BookOpen, testid: "nav-kfs" },
  ];

  // SSE: subscribe to /admin/stream for real-time notification toasts.
  useEffect(() => {
    if (!user || user === false) return undefined;
    const token = sessionStorage.getItem("nyk_token");
    const url = `${API}/admin/stream${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    let es;
    try {
      es = new EventSource(url, { withCredentials: true });
    } catch (_) {
      return undefined;
    }
    sseRef.current = es;
    es.addEventListener("notification", (ev) => {
      try {
        const n = JSON.parse(ev.data);
        if (!n.read_at) toast.message(n.title || "Notification", { description: n.body || "" });
      } catch (_) { /* ignore */ }
    });
    es.onerror = () => { /* silent reconnect handled by browser */ };
    return () => { es && es.close(); sseRef.current = null; };
  }, [user]);

  return (
    <div className="min-h-screen flex admin-shell" data-testid="admin-shell">
      <aside
        className="w-60 shrink-0 bg-white flex flex-col"
        style={{ borderRight: "1px solid rgba(17, 66, 50, 0.1)" }}
        data-testid="sidebar"
      >
        <div
          className="px-5 py-5 flex items-center gap-2.5"
          style={{ borderBottom: "1px solid rgba(17, 66, 50, 0.08)" }}
        >
          <img src="/fortiqa-mark.svg" alt="Fortiqa" className="w-9 h-9 object-contain shrink-0" data-testid="brand-logo" />
          <div>
            <span className="font-heading font-bold text-lg tracking-tight text-[#114232]">Fortiqa</span>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5c6360] font-semibold">{t("common.admin")}</div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3">
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 my-0.5 text-[13px] rounded-lg transition-all ${
                  isActive
                    ? "bg-[#114232] text-[#f4f2eb] font-semibold shadow-sm"
                    : "text-[#5c6360] hover:bg-[#114232]/6 hover:text-[#114232]"
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3" style={{ borderTop: "1px solid rgba(17, 66, 50, 0.08)" }}>
          <div className="px-1 pb-3 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">{t("language.label")}</span>
            <LanguageSwitcher compact />
          </div>
          <div className="px-2 py-2 text-xs">
            <div className="text-[#5c6360] uppercase tracking-wider font-semibold">{t("common.signed_in_as")}</div>
            <div className="font-medium text-[#1b1d1c] truncate">{user?.email}</div>
            <div className="text-[#5c6360] mt-0.5 capitalize">{user?.role?.replace(/_/g, " ")}</div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2.5 text-[13px] rounded-lg text-[#5c6360] hover:bg-[#c15337]/8 hover:text-[#c15337] transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.7} />
            <span>{t("auth.logout")}</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden flex flex-col" data-testid="admin-main">
        <div className="px-6 sm:px-8 pt-5 flex items-center justify-end gap-2.5" data-testid="admin-topbar">
          <ApiStatusBadge />
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("nyk:open-cmdk"))}
            data-testid="cmdk-trigger"
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#114232]/12 bg-white text-xs text-[#5c6360] hover:text-[#114232] hover:border-[#114232]/30 transition-colors"
            title="Open command palette (Cmd/Ctrl + K)"
          >
            <CommandIcon className="w-3.5 h-3.5" />
            <span>Search</span>
            <kbd className="font-mono text-[10px] bg-[#114232]/8 rounded px-1.5 py-0.5 text-[#114232]">⌘K</kbd>
          </button>
          <Link
            to="/"
            data-testid="admin-view-landing-link"
            title="Preview the public landing page"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#114232]/12 bg-white text-xs text-[#5c6360] hover:text-[#114232] hover:border-[#114232]/30 transition-colors"
          >
            <Home className="w-3.5 h-3.5" />
            <span>View landing</span>
          </Link>
          <NotificationsBell />
        </div>
        <CmdKPalette />
        <Outlet />
      </main>
    </div>
  );
}
